import { RecentApprovalsTicker } from "@/components/site/RecentApprovalsTicker";
import { FirstSessionPromise } from "@/components/site/FirstSessionPromise";
import { SchedulingGuideLink } from "@/components/site/SchedulingGuideLink";
import { CostOfRefusalCard } from "@/components/site/CostOfRefusalCard";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Search,
  MessageSquare,
  Brain,
  Rocket,
  ShieldCheck,
  CheckCircle2,
  FileCheck2,
  ArrowRight,
} from "lucide-react";
import { site } from "@/lib/site";
import { faqs } from "@/data/refusal-support-faqs";
import { openPlanChat } from "@/lib/plans";

export const Route = createFileRoute("/refusal-support")({
  head: () => ({
    meta: [
      { title: "US Visa Refusal Support - 214(b) Re-Attempt Coaching" },
      {
        name: "description",
        content:
          "Refused under 214(b)? Get root-cause analysis, red-flag fixes, DS-160 review, and 5 mock interviews for your re-attempt.",
      },
      {
        name: "keywords",
        content: "214(b) refusal, US visa rejected, F1 visa refusal, visa refusal coaching, re-attempt visa interview",
      },
      { property: "og:title", content: "US Visa Refusal Support - 214(b) Re-Attempt Coaching" },
      {
        property: "og:description",
        content:
          "Root-cause analysis + 5 mocks + DS-160 review + red-flag fixes. Built for re-attempts after a 214(b) refusal.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://welivdit.com/refusal-support" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/refusal-support" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "US Visa Refusal Support",
          provider: { "@type": "Organization", name: "WeLivdIt" },
          serviceType: "214(b) refusal coaching and re-attempt preparation",
          offers: { "@type": "Offer", price: "7000", priceCurrency: "INR" },
          areaServed: "Worldwide",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: RefusalSupportPage,
});

const includes = [
  {
    icon: Search,
    title: "Refusal Root-Cause Analysis",
    desc: "We walk through your last interview minute-by-minute to find what actually triggered the 214(b) - not just what you think went wrong.",
  },
  {
    icon: AlertTriangle,
    title: "Red-Flag Identification & Fixes",
    desc: "Profile, financials, ties, intent, prior travel - we map every red flag the officer likely saw and build a counter-narrative for each.",
  },
  {
    icon: FileCheck2,
    title: "Full DS-160 Re-Review",
    desc: "Every field re-checked against your spoken story. Inconsistencies are the #1 cause of repeat refusals.",
  },
  {
    icon: Brain,
    title: "Officer Logic & Strategy Session",
    desc: "1-on-1 deep dive into how visa officers actually evaluate re-applicants - the bar is higher the second time.",
  },
  {
    icon: MessageSquare,
    title: "5 One-on-One Mock Interviews",
    desc: "Five live mocks with feedback. We escalate difficulty until you handle the curveballs that broke your last interview.",
  },
  {
    icon: Rocket,
    title: "Re-Attempt Game Plan",
    desc: "Day-of-interview script: opening line, document order, what to volunteer, what to keep brief. Nothing left to chance.",
  },
];


function RefusalSupportPage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-gradient-hero">
        <div className="mx-auto max-w-5xl px-5 pt-16 pb-12 md:pt-24 md:pb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900 text-xs font-medium">
            <AlertTriangle className="h-3.5 w-3.5" /> 214(b) Refusal Support
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-balance max-w-3xl">
            Refused under 214(b)? Let's land the re-attempt.
          </h1>
          <p className="mt-5 text-lg text-foreground/70 max-w-2xl leading-relaxed">
            A second refusal is harder to come back from than a first. We rebuild your case from the ground up -
            root-cause analysis, red-flag fixes, DS-160 re-review, and five hard mocks until your story is bulletproof.
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Backed by The WeLivdIt Method
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => openPlanChat("refusal")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition"
            >
              Enroll in Refusal Support <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            ₹7,000 one-time · 5 mocks · DS-160 re-review · Refusal analysis included
          </p>
          <FirstSessionPromise variant="inline" className="mt-3" />

        </div>
      </section>

      {/* Why re-attempts fail */}
      <section className="mx-auto max-w-5xl px-5 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">Why most re-attempts fail</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
            Re-applying without changing anything = same result.
          </h2>
          <p className="mt-4 text-foreground/70 leading-relaxed">
            Officers can see your refusal history. If your story, DS-160, and answers look identical to last time, the
            decision usually does too. The fix isn't a better script - it's a materially stronger case.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            "Same DS-160 with the same inconsistencies",
            "No new ties to home country surfaced",
            "Financials still look thin or unclear",
            "Story for chosen course/university still vague",
            "Same nervous delivery, same filler answers",
            "No explanation prepared for previous refusal",
          ].map((item) => (
            <div key={item} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <span className="text-sm text-foreground/80">{item}</span>
            </div>
          ))}
        </div>
      </section>

      <RecentApprovalsTicker />
      <CostOfRefusalCard className="pt-0" />
      <SchedulingGuideLink className="my-10" />


      {/* What's included */}
      <section className="bg-muted/30 border-y border-border">
        <div className="mx-auto max-w-5xl px-5 py-16">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-medium text-primary uppercase tracking-wider">What's included</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
              Everything you need for a clean re-attempt.
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {includes.map((it) => (
              <div key={it.title} className="rounded-3xl border border-border bg-card p-6">
                <div className="h-11 w-11 rounded-2xl bg-accent grid place-items-center text-primary">
                  <it.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">{it.title}</h3>
                <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Process */}
      <section className="mx-auto max-w-5xl px-5 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">How it works</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
            From refusal to re-interview, in 3 steps.
          </h2>
        </div>

        <ol className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[
            {
              n: 1,
              title: "Refusal Analysis & DS-160 Audit",
              desc: "We reconstruct your previous interview question-by-question to identify what most likely led to your refusal. Then we thoroughly review your DS-160 for inconsistencies and recommend profile-specific improvements before your re-attempt.",
            },
            {
              n: 2,
              title: "5 Progressive Mock Interviews",
              desc: "Each mock gets tougher than the last-from fundamentals to officer-style curveballs-until you're ready to confidently handle the real interview.",
            },
            {
              n: 3,
              title: "Re-Interview Strategy",
              desc: "We finalize your opening, document flow, body language, and how to confidently address your previous refusal if the officer brings it up.",
            },
          ].map((step) => (
            <li key={step.n} className="rounded-3xl border border-border bg-card p-6">
              <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground grid place-items-center font-display font-semibold">
                {step.n}
              </div>

              <h3 className="mt-4 font-display text-lg font-semibold">{step.title}</h3>

              <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{step.desc}</p>
            </li>
          ))}
        </ol>
      </section>
      {/* Who it's for */}
      <section className="mx-auto max-w-5xl px-5 pb-16">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-3xl border border-border bg-card p-7">
            <div className="inline-flex items-center gap-2 text-emerald-700 text-sm font-semibold">
              <CheckCircle2 className="h-4 w-4" /> This is for you if
            </div>
            <ul className="mt-4 space-y-3 text-sm text-foreground/80">
              {[
                "You've been refused under 214(b) at least once",
                "Your re-interview is 3+ weeks away",
                "You're willing to do honest work on weak parts of your profile",
                "You want a real Specialist, not a recorded course",
              ].map((i) => (
                <li key={i} className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                  {i}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-border bg-card p-7">
            <div className="inline-flex items-center gap-2 text-amber-700 text-sm font-semibold">
              <AlertTriangle className="h-4 w-4" /> This is NOT for you if
            </div>
            <ul className="mt-4 space-y-3 text-sm text-foreground/80">
              {[
                "Your interview is in less than 5 days (we won't have time to do this right)",
                "You're a first-time applicant - pick Full Support instead",
                "You want a guaranteed approval (no honest Specialist offers that)",
                "You need help with SEVIS, I-20, or admissions (we don't do those)",
              ].map((i) => (
                <li key={i} className="flex gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  {i}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/30 border-y border-border">
        <div className="mx-auto max-w-3xl px-5 py-16">
          <div className="text-center">
            <p className="text-sm font-medium text-primary uppercase tracking-wider">FAQ</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Common questions about refusal support
            </h2>
          </div>
          <div className="mt-10 space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-2xl border border-border bg-card p-5">
                <summary className="cursor-pointer list-none font-display text-base font-semibold flex items-center justify-between">
                  {f.q}
                  <span className="ml-4 text-muted-foreground group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-foreground/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-ink-foreground">
        <div className="mx-auto max-w-3xl px-5 py-20 text-center">
          <ShieldCheck className="h-10 w-10 text-primary mx-auto" />
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tight">
            Don't walk in unprepared. Again.
          </h2>
          <p className="mt-4 text-ink-foreground/70 max-w-xl mx-auto">
            ₹7,000 · 5 one-on-one mocks · DS-160 re-review · Full refusal analysis. A Specialist reaches out on WhatsApp
            within 24 hours of enrollment.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => openPlanChat("refusal")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition"
            >
              Enroll in Refusal Support <ArrowRight className="h-4 w-4" />
            </button>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/20 bg-white/[0.04] text-ink-foreground font-medium hover:bg-white/[0.08] transition"
            >
              Compare all plans
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
